import type { FC } from 'react';
import { useMemo } from 'react';
import { Badge, Collapse, Flex, Typography } from 'antd';
import { ClusterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import {
  OPTIMIZER_ACCORDION_CONTENT_ID,
  OPTIMIZER_ACCORDION_SECTION_ID,
  OPTIMIZER_KANBAN_PREVIEW_LIMIT,
} from '~/app/components/maintenance/work-orders/optimizer/constants';
import { OptimizerSuggestionsBody } from '~/app/components/maintenance/work-orders/optimizer/components/OptimizerSuggestionsBody/OptimizerSuggestionsBody';
import { useOptimizerMergeSuggestions } from '~/app/components/maintenance/work-orders/optimizer/hooks/useOptimizerMergeSuggestions';
import type { OptimizerWorkOrderLabel } from '~/app/components/maintenance/work-orders/optimizer/types';
import type { KanbanWorkOrderView } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';

export interface WorkOrderOptimizerAccordionProps {
  organizationId: string;
  siteFilter: string;
  workOrders: KanbanWorkOrderView[];
  siteNameById: Map<string, string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectWorkOrder: (workOrderId: string) => void;
}

export const WorkOrderOptimizerAccordion: FC<
  WorkOrderOptimizerAccordionProps
> = ({
  organizationId,
  siteFilter,
  workOrders,
  siteNameById,
  open,
  onOpenChange,
  onSelectWorkOrder,
}) => {
  const { t } = useTranslation('maintenance');

  const workOrderById = useMemo(() => {
    const map = new Map<string, OptimizerWorkOrderLabel>();
    for (const wo of workOrders) {
      map.set(wo.id, { id: wo.id, code: wo.code, status: wo.status });
    }
    return map;
  }, [workOrders]);

  const list = useOptimizerMergeSuggestions({
    organizationId,
    siteFilter,
    pageSize: OPTIMIZER_KANBAN_PREVIEW_LIMIT,
    workOrderById,
    siteNameById,
    lockToFirstPage: true,
  });

  return (
    <div id={OPTIMIZER_ACCORDION_SECTION_ID} style={{ scrollMarginTop: 72 }}>
      <Collapse
        activeKey={open ? ['optimizer'] : []}
        onChange={(keys) => {
          const next = Array.isArray(keys) ? keys : [keys];
          onOpenChange(next.includes('optimizer'));
        }}
        expandIconPlacement="end"
        style={{
          background: '#fff',
          border: `1px solid ${WORK_ORDER_UI.cardBorder}`,
          borderRadius: 12,
        }}
        items={[
          {
            key: 'optimizer',
            label: (
              <Flex align="center" gap={10} wrap="wrap">
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background:
                      list.total > 0 ? '#ecfdf5' : WORK_ORDER_UI.pageBg,
                    color:
                      list.total > 0
                        ? WORK_ORDER_UI.primary
                        : WORK_ORDER_UI.sidebarNavy,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ClusterOutlined />
                </span>
                <div>
                  <Flex align="center" gap={8}>
                    <Typography.Text
                      strong
                      style={{ color: WORK_ORDER_UI.sidebarNavy }}
                    >
                      {t('workOrders.kanban.optimizer.title')}
                    </Typography.Text>
                    {!list.isLoading ? (
                      <Badge
                        count={list.total}
                        overflowCount={999}
                        showZero
                        color={
                          list.total > 0 ? WORK_ORDER_UI.primary : '#94a3b8'
                        }
                        style={{ fontWeight: 700 }}
                      />
                    ) : null}
                  </Flex>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {list.total > 0
                      ? t('workOrders.kanban.optimizer.accordionHint', {
                          total: list.total,
                          preview: OPTIMIZER_KANBAN_PREVIEW_LIMIT,
                        })
                      : t('workOrders.kanban.optimizer.description')}
                  </Typography.Text>
                </div>
              </Flex>
            ),
            children: (
              <div
                id={OPTIMIZER_ACCORDION_CONTENT_ID}
                style={{ scrollMarginTop: 72 }}
              >
                <OptimizerSuggestionsBody
                  clusters={list.clusters}
                  canManage={list.canManage}
                  isLoading={list.isLoading}
                  isError={list.isError}
                  total={list.total}
                  page={list.page}
                  pageSize={OPTIMIZER_KANBAN_PREVIEW_LIMIT}
                  showPreviewFooter
                  previewLimit={OPTIMIZER_KANBAN_PREVIEW_LIMIT}
                  siteFilter={siteFilter}
                  pendingApply={list.pendingApply}
                  isApplying={list.isApplying}
                  onPageChange={list.setPage}
                  onSelectWorkOrder={onSelectWorkOrder}
                  onOpenApply={list.openApply}
                  onPendingTargetChange={(id) =>
                    list.setPendingApply((prev) =>
                      prev ? { ...prev, targetWorkOrderId: id } : prev,
                    )
                  }
                  onCancelApply={() => list.setPendingApply(null)}
                  onConfirmApply={() => {
                    void list.confirmApply();
                  }}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
